import React, { useEffect, useState } from "react";
import { EventCalendarProps } from "./EventCalendarProps.csharp";
import { ICardViewModel } from "../../../Partials/Card/ICardViewModel.csharp";
import { HappeningsPaginationModel } from "./HappeningsPaginationModel.csharp";
import { EventCalendarFilters, defaultSelectValue } from "./EventCalendarFilters";
import { EventsList } from "./EventsList";
import { ExhibitionsList } from "./ExhibitionsList";
import { Option } from "react-dropdown";
import { Tabs, TabDefinition } from "../../../Partials/Tabs/Tabs";

export const EventCalendar: React.FC<EventCalendarProps> = ({
    model,
    translations,
    profile,
    endpointUrl,
    calendarLocalization,
}) => {
    const [activeTab, setActiveTab] = useState<"events" | "exhibitions">("events");
    const [currentCategory, setCurrentCategory] = useState(defaultSelectValue);
    const [categoryLabel, setCategoryLabel] = useState("");
    const [currentDate, setCurrentDate] = useState<Date | null>(null);
    const [currentEndDate, setCurrentEndDate] = useState<Date | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [eventGroups, setEventGroups] = useState<any[]>(() => {
        return model.happenings.events.groups || [];
    });
    const [currentPageNumber, setCurrentPageNumber] = useState(model.happenings.events.pageNumber);
    const [hasNextPage, setHasNextPage] = useState(model.happenings.events.hasNext);
    const [pageSize] = useState(model.happenings.events.pageSize);
    const [totalCount, setTotalCount] = useState(model.happenings.events.totalCount || 0);

    const loadResults = (category: string, fromDate: Date, endDate: Date | null, pageNumber: number) => {
        setIsLoading(true);

        const fetchCategory = category === defaultSelectValue ? "" : category;
        const fromDateStr = fromDate.toISOString().split("T")[0];
        const endDateStr = endDate ? endDate.toISOString().split("T")[0] : "";

        let url =
            `${endpointUrl}?eventCalendarId=${model.id}&lang=${calendarLocalization.cultureName}&category=${fetchCategory}` +
            `&pageSize=${pageSize}&pageNumber=${pageNumber}&fromDate=${fromDateStr}`;

        if (endDateStr) {
            url += `&endDate=${endDateStr}`;
        }

        fetch(url, { credentials: "same-origin" })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((result: HappeningsPaginationModel) => {
                const groups = result.events.groups || [];

                if (pageNumber === 1) {
                    setEventGroups(groups);
                } else {
                    setEventGroups((prevGroups) => {
                        const updatedGroups = [...prevGroups];
                        groups.forEach((newGroup: any) => {
                            const existingGroupIndex = updatedGroups.findIndex((g) => g.title === newGroup.title);
                            if (existingGroupIndex >= 0) {
                                updatedGroups[existingGroupIndex].items = [
                                    ...updatedGroups[existingGroupIndex].items,
                                    ...newGroup.items,
                                ];
                            } else {
                                updatedGroups.push(newGroup);
                            }
                        });
                        return updatedGroups;
                    });
                }

                setCurrentPageNumber(result.events.pageNumber);
                setHasNextPage(result.events.hasNext);
                setTotalCount(result.events.totalCount || 0);
                setIsLoading(false);
                // Don't reset isSearching here - it should remain true if filters are applied
            })
            .catch((error) => {
                console.error("Error loading results:", error);
                setIsLoading(false);
                // Don't reset isSearching here either - it should remain in its current state
            });

        updateURL(category, activeTab, fromDate, endDate);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const selectedTab = urlParams.get("tab");
        let selectedCategory = urlParams.get("category");
        const selectedFromDateStr = urlParams.get("fromDate") || urlParams.get("date"); // Support legacy 'date' param
        const selectedEndDateStr = urlParams.get("endDate");

        const categoryExists = model.categories.some((c) => c.value.toLowerCase() === selectedCategory?.toLowerCase());
        if (categoryExists && selectedCategory) {
            const foundCategory = model.categories.find(
                (c) => c.value.toLowerCase() === selectedCategory?.toLowerCase()
            );
            selectedCategory = foundCategory?.value || defaultSelectValue;
            // Set the category label if a valid category is found
            if (foundCategory && selectedCategory !== defaultSelectValue) {
                setCategoryLabel(foundCategory.label);
            }
        } else {
            selectedCategory = defaultSelectValue;
        }

        const tab = selectedTab === "exhibitions" ? "exhibitions" : "events";
        setActiveTab(tab);
        setCurrentCategory(selectedCategory);

        if (selectedFromDateStr) {
            const parsedFromDate = new Date(selectedFromDateStr);
            if (!isNaN(parsedFromDate.getTime())) {
                setCurrentDate(parsedFromDate);

                let parsedEndDate: Date | null = null;
                if (selectedEndDateStr) {
                    parsedEndDate = new Date(selectedEndDateStr);
                    if (!isNaN(parsedEndDate.getTime())) {
                        setCurrentEndDate(parsedEndDate);
                    }
                }

                setIsSearching(true);
                loadResults(selectedCategory, parsedFromDate, parsedEndDate, 1);
            }
        } else {
            if (selectedCategory !== defaultSelectValue) {
                const today = new Date();
                setIsSearching(true);
                loadResults(selectedCategory, today, null, 1);
            }
        }
    }, [model.categories]);

    const updateURL = (category: string, tab: string, fromDate?: Date | null, endDate?: Date | null) => {
        const baseUrl = window.location.href.split("?")[0];
        const params = new URLSearchParams();
        if (category && category !== defaultSelectValue) params.set("category", category);
        if (tab) params.set("tab", tab);
        if (fromDate) params.set("fromDate", fromDate.toISOString().split("T")[0]);
        if (endDate) params.set("endDate", endDate.toISOString().split("T")[0]);
        history.pushState({}, "", `${baseUrl}?${params.toString()}`);
    };

    const handleCategoryChange = (selectedOption: Option) => {
        const newCategory = selectedOption.value;
        setCurrentCategory(newCategory);
        setCategoryLabel(selectedOption.label.toString());
        setIsSearching(true);

        const dateToUse = currentDate ?? new Date();
        loadResults(newCategory, dateToUse, currentEndDate, 1);
    };

    const handleDateChange = (date: Date) => {
        setCurrentDate(date);
        setCurrentEndDate(null);
        setIsSearching(true);
        loadResults(currentCategory, date, null, 1);
        updateURL(currentCategory, activeTab, date, null);
    };

    const handleDateRangeChange = (range: { from: Date | undefined; to: Date | undefined }) => {
        if (range.from) {
            setCurrentDate(range.from);
            setCurrentEndDate(range.to || null);
            setIsSearching(true);
            loadResults(currentCategory, range.from, range.to || null, 1);
            updateURL(currentCategory, activeTab, range.from, range.to || null);
        }
    };

    const clearFilters = () => {
        const defaultCategory = defaultSelectValue;
        setCurrentCategory(defaultCategory);
        setCategoryLabel("");
        setCurrentDate(null);
        setCurrentEndDate(null);
        setIsSearching(false);
        // Reset to initial events from model
        const groups = model.happenings.events.groups || [];
        setEventGroups(groups);
        setCurrentPageNumber(model.happenings.events.pageNumber);
        setHasNextPage(model.happenings.events.hasNext);
        setTotalCount(model.happenings.events.totalCount || 0);
        updateURL(defaultCategory, activeTab, null, null);
    };

    const loadMore = () => {
        const dateToUse = currentDate ?? new Date();
        loadResults(currentCategory, dateToUse, currentEndDate, currentPageNumber + 1);
    };

    const tabs: TabDefinition[] = [
        {
            id: "events",
            label: translations.events,
            content: (
                <>
                    <EventCalendarFilters
                        translations={translations}
                        categories={model.categories}
                        currentCategory={currentCategory}
                        currentDate={currentDate ?? null}
                        currentEndDate={currentEndDate}
                        onCategoryChange={handleCategoryChange}
                        onDateChange={handleDateChange}
                        isLoading={isLoading}
                        isSearching={isSearching}
                        localization={calendarLocalization}
                        totalResults={totalCount}
                        categoryLabel={categoryLabel}
                        onRangeChange={handleDateRangeChange}
                        onClearFilters={clearFilters}
                    />
                    <div className="EventCalendar__container">
                        <div className="EventCalendar__content">
                            <EventsList
                                eventGroups={eventGroups}
                                translations={translations}
                                profile={profile}
                                isLoading={isLoading}
                                hasNextPage={hasNextPage}
                                loadMore={loadMore}
                                pageSize={pageSize}
                                totalCount={totalCount}
                            />
                        </div>
                    </div>
                </>
            ),
        },
        {
            id: "exhibitions",
            label: translations.exhibitions,
            content: (
                <div className="EventCalendar__container">
                    <ExhibitionsList
                        profile={profile}
                        translations={translations}
                        currentExhibitions={model.happenings.currentExhibitions || []}
                        futureExhibitions={model.happenings.futureExhibitions || []}
                        pastExhibitions={model.happenings.pastExhibitions || []}
                        allExhibitionsUrl={model.allExhibitionsUrl}
                        collectionExhibitionsCheckboxText={model.happenings.collectionExhibitionsCheckboxText}
                        temporaryExhibitionsCheckboxText={model.happenings.temporaryExhibitionsCheckboxText}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className="EventCalendar">
            <h1 className="EventCalendar__pageTitle">{model.title}</h1>

            <Tabs
                tabs={tabs}
                activeTabId={activeTab}
                onTabChange={(id) => {
                    setActiveTab(id as "events" | "exhibitions");
                    updateURL(currentCategory, id, currentDate, currentEndDate);
                }}
            />
        </div>
    );
};
